import React from 'react';
import UserAvatar from './UserAvatar';

/**
 * Podium — the top three, on the weekly board and the season board.
 *
 * Both pages used to draw this by hand, and both had the same two problems.
 * The box under each player held a bare digit, which on a board full of
 * numbers is one more number; and the score sat above the box in small grey
 * text, the least distinct thing in the whole block, when it is the one figure
 * the podium exists to show.
 *
 * So the box now carries the score as its headline, big and white, with the
 * word under it saying what it counts, and the place is spelled out with its
 * medal at the foot. A digit says "3". A medal and 3RD say third place.
 *
 * `rows` is the board, already ordered; only the first three are drawn, in the
 * 2 / 1 / 3 arrangement a podium has. The place comes from the POSITION on the
 * board, never from a shared rank: one gold box, one silver, one bronze, every
 * time. The first version honoured ties and put three gold 1ST boxes up when
 * three people had the same score, which read as three winners. The board
 * itself already orders a tie (over/under on the tiebreaker, then closeness,
 * then name), so the podium follows that order and names one person first.
 * The table below it still shows a shared rank, which is where a tie belongs.
 *
 * `unit` is what the number counts: "correct" on both boards today.
 */
const TIERS = {
  1: { medal: '🥇', place: '1ST', color: '#FFD700', box: 'linear-gradient(180deg,#b8860b,#8b6508)', height: 132, avatar: 64, name: 16, score: 36, glow: '0 0 20px rgba(255,215,0,0.2)', width: 180 },
  2: { medal: '🥈', place: '2ND', color: '#C0C0C0', box: 'linear-gradient(180deg,#5a6472,#3a424a)', height: 112, avatar: 52, name: 14, score: 32, glow: 'none', width: 160 },
  3: { medal: '🥉', place: '3RD', color: '#CD7F32', box: 'linear-gradient(180deg,#7a4010,#4a2508)', height: 96,  avatar: 52, name: 14, score: 28, glow: 'none', width: 160 },
};

function Column({ row, position, unit }) {
  if (!row) return null;
  const tier = TIERS[position];
  const score = row.score;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, maxWidth: tier.width }}>
      <div style={{ position: 'relative', marginBottom: 2 }}>
        {position === 1 && (
          <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', fontSize: 22 }}>👑</div>
        )}
        <UserAvatar username={row.username} size={tier.avatar}
          style={position === 1 ? { boxShadow: '0 0 20px rgba(255,215,0,0.4)' } : undefined} />
      </div>
      <div style={{
        marginTop: position === 1 ? 14 : 10, marginBottom: 8,
        fontFamily: "'Oswald',sans-serif", fontSize: tier.name, fontWeight: 700, color: tier.color,
        letterSpacing: 0.5, textAlign: 'center', maxWidth: tier.width - 40,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
      }}>{row.username}</div>

      <div style={{
        width: '100%', height: tier.height, background: tier.box,
        borderRadius: '8px 8px 0 0', boxShadow: tier.glow,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2
      }}>
        {/* The score is the headline. White, not the medal colour: gold text
            on a gold box was the old problem in a different place. */}
        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: tier.score, fontWeight: 700, color: '#fff', lineHeight: 1, textShadow: '0 2px 6px rgba(0,0,0,0.45)' }}>
          {score}
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase' }}>
          {unit}
        </div>
        <div style={{
          marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.18)', width: '70%',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          fontFamily: "'Oswald',sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 1, color: tier.color
        }}>
          <span style={{ fontSize: 15 }}>{tier.medal}</span>{tier.place}
        </div>
      </div>
    </div>
  );
}

export default function Podium({ rows, unit = 'correct' }) {
  if (!rows || rows.length < 2) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 12, marginBottom: 28, padding: '0 8px' }}>
      <Column row={rows[1]} position={2} unit={unit} />
      <Column row={rows[0]} position={1} unit={unit} />
      <Column row={rows[2]} position={3} unit={unit} />
    </div>
  );
}
